import { createApi } from '@reduxjs/toolkit/query/react';
import { DateTime } from 'luxon';

import { saveResponseUsingHiddenElement } from 'src/common/util/saveResponseUsingHiddenElement';
import { parseInvoiceExport } from 'src/features/billing/invoices/parseInvoiceExport';
import { ParsedField } from 'src/features/reports/CustomReportsRoute/FieldListEditor';
import { bambiBaseQuery } from './bambiBaseQuery';
import { transformCustomTripReport } from './transformers/transformCustomTripReport';

// initialize an empty api service that we'll inject endpoints into later as needed
export const emptySplitApi = createApi({
  baseQuery: bambiBaseQuery,
  endpoints: () => ({}),
});

export type InvoiceExportCSVArgOrdering =
  | 'trip__passenger__full_name'
  | 'trip__time_range__startswith'
  | 'trip__payer__display_name'
  // Sentinel accepted by the backend to explicitly disable sorting
  // (an absent ordering param falls back to the default trip grouping).
  | '-1';

export type InvoiceExportCSVArg = {
  rangeStart: string;
  rangeEnd?: string;
  invoice?: string;
  tripPayerIn?: string[];
  organizationId?: string;
  outputType?: 'quickbooks' | 'condensed' | 'quickbooks-self-pay';
  format: 'csv' | 'pdf';
  overallPaymentStatusIn?: (
    | 'overpaid'
    | 'paid'
    | 'partially_paid'
    | 'unknown'
    | 'unpaid'
  )[];
  ordering?: InvoiceExportCSVArgOrdering;
};

export type InvoiceSelectedListExportCSVArg = {
  organizationId?: string;
  selectedInvoices: string[];
  format: 'csv' | 'pdf';
  outputType?: 'quickbooks' | 'condensed';
  ordering?: InvoiceExportCSVArgOrdering;
};

export type InvoicePaymentSelectedListExportCSVArg = {
  organizationId?: string;
  selectedInvoices: string[];
  format: 'csv' | 'pdf';
  start_date?: string;
  end_date?: string;
};

export type InvoiceRefundSelectedListExportCSVArg =
  InvoicePaymentSelectedListExportCSVArg;

const customTripEndpoints = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    // We do not want to cache files in RTKQuery as we could crash the browser
    // The code gen also does not make it possible to parse the response as a blob
    // For more info: https://github.com/reduxjs/redux-toolkit/issues/1522
    getTripsExportAsCsv: builder.query({
      keepUnusedDataFor: 0,
      queryFn: async (
        {
          rangeStart,
          rangeEnd,
          payers,
          passengers,
          outputSet,
          organizationId,
          format,
          status,
          driverIn,
          externalTripIds,
          tripIds,
          fields,
        }: {
          rangeStart?: string;
          rangeEnd?: string;
          payers?: string;
          passengers?: string;
          outputSet?: string;
          organizationId?: string;
          format?: string;
          status?: string;
          driverIn?: string;
          externalTripIds?: string;
          tripIds?: string;
          fields?: ParsedField[];
        },
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const result = await baseQuery({
          url: `/trips/export/?range_start=${rangeStart || ''}&range_end=${
            rangeEnd || ''
          }&payers=${payers || ''}&passengers=${passengers || ''}&output_set=${
            outputSet || ''
          }${status ? '&status=' + status : ''}${
            driverIn ? '&driver__in=' + driverIn : ''
          }${
            externalTripIds ? '&external_trip_id__in=' + externalTripIds : ''
          }${tripIds ? '&id__in=' + tripIds : ''}`,
          method: 'GET',
          headers: {
            'organization-id': organizationId,
          },
          responseHandler: (response) => {
            if (format && format === 'pdf') {
              return response.text();
            }
            return response.blob();
          },
        });

        // @ts-ignore
        const responseHeaders: Headers = result.meta?.response?.headers;
        const fallback = `trip_export_${DateTime.now().toFormat(
          'MMddyyyyHHmmss'
        )}.csv`;
        const fileName = getFilename(responseHeaders, fallback);

        // PDF exports are handled separately
        if (format && format === 'pdf') {
          return {
            data: {
              records: result.data,
              fileName,
            },
          };
        }

        let finalResult = result;

        if (fields) {
          const data = await (result.data as Blob).text();
          finalResult.data = transformCustomTripReport(fields, data);
        }

        saveResponseUsingHiddenElement(finalResult, fileName);
        return { data: null };
      },
    }),
    getTripsExportAsTxt: builder.query({
      keepUnusedDataFor: 0,
      queryFn: async (
        {
          rangeStart,
          rangeEnd,
          payers,
          passengers,
          outputSet,
          organizationId,
          format,
          status,
          driverIn,
          externalTripIds,
          tripIds,
        }: {
          rangeStart?: string;
          rangeEnd?: string;
          payers?: string;
          passengers?: string;
          outputSet?: string;
          organizationId?: string;
          format?: string;
          status?: string;
          driverIn?: string;
          externalTripIds?: string;
          tripIds?: string;
        },
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const result = await baseQuery({
          url: `/trips/export/?range_start=${rangeStart || ''}&range_end=${
            rangeEnd || ''
          }&payers=${payers || ''}&passengers=${passengers || ''}&output_set=${
            outputSet || ''
          }${status ? '&status=' + status : ''}${
            driverIn ? '&driver__in=' + driverIn : ''
          }${
            externalTripIds ? '&external_trip_id__in=' + externalTripIds : ''
          }${tripIds ? '&id__in=' + tripIds : ''}`,
          method: 'GET',
          headers: {
            'organization-id': organizationId,
          },
          responseHandler: (response) => {
            if (format && format === 'pdf') {
              return response.text();
            }
            return response.blob();
          },
        });

        // @ts-ignore
        const responseHeaders: Headers = result.meta?.response?.headers;
        const fallback = `trip_export_${DateTime.now().toFormat(
          'MMddyyyyHHmmss'
        )}.txt`;
        const fileName = getFilename(responseHeaders, fallback);

        // PDF exports are handled separately
        if (format && format === 'pdf') {
          return {
            data: {
              records: result.data,
              fileName,
            },
          };
        }

        saveResponseUsingHiddenElement(result, fileName);
        return { data: null };
      },
    }),
    getInvoicesExportListAsCsv: builder.query({
      keepUnusedDataFor: 0,
      queryFn: async (
        {
          rangeStart,
          rangeEnd,
          tripPayerIn,
          invoice,
          overallPaymentStatusIn,
          organizationId,
          outputType,
          format = 'csv',
          ordering,
        }: InvoiceExportCSVArg,
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const result = await baseQuery({
          url: `/invoices/export/?range_start=${rangeStart || ''}&range_end=${
            rangeEnd || ''
          }&trip__payer__in=${tripPayerIn || ''}&invoice=${
            invoice || ''
          }&output_type=${
            outputType ?? 'quickbooks'
          }&overall_payment_status__in=${
            overallPaymentStatusIn?.join(',') ?? ''
          }&ordering=${ordering ?? ''}`,
          method: 'GET',
          headers: {
            'organization-id': organizationId,
          },
          responseHandler: (response) => response.blob(),
        });

        // @ts-ignore
        const responseHeaders: Headers = result.meta?.response?.headers;
        const fallback = `invoice_list_export_${DateTime.now().toFormat(
          'MMddyyyyHHmmss'
        )}.csv`;
        const fileName = getFilename(responseHeaders, fallback);

        if (format !== 'csv') {
          const records = await (result.data as Blob)?.text();
          return parseInvoiceExport(records, fileName);
        }

        saveResponseUsingHiddenElement(result, fileName);
        return { data: null };
      },
    }),
    getInvoicesExportListBySelectedInvoiceCsv: builder.query({
      keepUnusedDataFor: 0,
      queryFn: async (
        {
          organizationId,
          selectedInvoices,
          outputType,
          format = 'csv',
          ordering,
        }: InvoiceSelectedListExportCSVArg,
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const result = await baseQuery({
          url: `/invoices/export/list-selected/?output_type=${
            outputType ?? 'quickbooks'
          }&ordering=${ordering ?? ''}`,
          method: 'POST',
          headers: {
            'organization-id': organizationId,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            selected_invoices: selectedInvoices,
          }),
          responseHandler: (response) => response.blob(),
        });

        // @ts-ignore
        const responseHeaders: Headers = result.meta?.response?.headers;
        const fallback = `invoice_list_export_${DateTime.now().toFormat(
          'MMddyyyyHHmmss'
        )}.csv`;
        const fileName = getFilename(responseHeaders, fallback);

        if (format !== 'csv') {
          const records = await (result.data as Blob)?.text();
          return parseInvoiceExport(records, fileName);
        }

        saveResponseUsingHiddenElement(result, fileName);
        return { data: null };
      },
    }),
    getInvoicesPaymentsExportListBySelectedInvoiceCsv: builder.query({
      keepUnusedDataFor: 0,
      queryFn: async (
        {
          organizationId,
          selectedInvoices,
          format = 'csv',
          start_date,
          end_date,
        }: InvoicePaymentSelectedListExportCSVArg,
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const result = await baseQuery({
          url: `/invoices/export-payments/list-selected/`,
          method: 'POST',
          headers: {
            'organization-id': organizationId,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            selected_invoices: selectedInvoices,
            start_date,
            end_date,
          }),
          responseHandler: (response) => response.blob(),
        });

        // @ts-ignore
        const responseHeaders: Headers = result.meta?.response?.headers;
        const fallback = `invoice_payments_list_export_${DateTime.now().toFormat(
          'MMddyyyyHHmmss'
        )}.csv`;
        const fileName = getFilename(responseHeaders, fallback);

        if (format !== 'csv') {
          const records = await (result.data as Blob)?.text();
          return parseInvoiceExport(records, fileName);
        }

        saveResponseUsingHiddenElement(result, fileName);
        return { data: null };
      },
    }),
    getInvoicesRefundsExportListBySelectedInvoiceCsv: builder.query({
      keepUnusedDataFor: 0,
      queryFn: async (
        {
          organizationId,
          selectedInvoices,
          format = 'csv',
        }: InvoiceRefundSelectedListExportCSVArg,
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const result = await baseQuery({
          url: `/invoices/export-refunds/list-selected/`,
          method: 'POST',
          headers: {
            'organization-id': organizationId,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            selected_invoices: selectedInvoices,
          }),
          responseHandler: (response) => response.blob(),
        });

        // @ts-ignore
        const responseHeaders: Headers = result.meta?.response?.headers;
        const fallback = `invoice_payments_list_export_${DateTime.now().toFormat(
          'MMddyyyyHHmmss'
        )}.csv`;
        const fileName = getFilename(responseHeaders, fallback);

        if (format !== 'csv') {
          const records = await (result.data as Blob)?.text();
          return parseInvoiceExport(records, fileName);
        }

        saveResponseUsingHiddenElement(result, fileName);
        return { data: null };
      },
    }),
  }),
});

function getFilename(headers: Headers, fallback: string): string {
  const content = headers?.get('content-disposition');
  if (!content) {
    return fallback;
  }

  const filename = content.match(/filename="(.*)?"/);
  if (!filename || !filename[1]) {
    return fallback;
  }

  return filename[1];
}

export const {
  useLazyGetTripsExportAsCsvQuery,
  useLazyGetTripsExportAsTxtQuery,
  useLazyGetInvoicesExportListAsCsvQuery,
  useLazyGetInvoicesExportListBySelectedInvoiceCsvQuery,
  useLazyGetInvoicesPaymentsExportListBySelectedInvoiceCsvQuery,
  useLazyGetInvoicesRefundsExportListBySelectedInvoiceCsvQuery,
} = customTripEndpoints;
